// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import setCanSendReturnRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetCanSendReturnRequest";
import setOrderLines from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetOrderLines";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartLinesListHeader from "@insite/content-library/Components/CartLinesListHeader";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer from "@sc/App/Components/Cart/CartLineContainer";
import LinesHeader from "@sc/App/Components/Cart/LinesHeader";
import QtyField, { QtyFieldStyles } from "@sc/App/Components/Qty/QtyField";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    orderLines: state.pages.requestRma.orderLines,
    // XNG-Change
    hidePrices: hidePrice(state),
});

const mapDispatchToProps = {
    setOrderLines,
    setCanSendReturnRequest,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestRmaProductListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    returnReasonGridItem?: GridItemProps;
    qtyToReturnTextField?: TextFieldPresentationProps;
    returnReasonSelect?: SelectPresentationProps;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productBrandStyles?: ProductBrandStyles;
    productInfoPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoSubtotalGridItem?: GridItemProps;
    // XNG-Changes
    priceStyles?: SmallHeadingAndTextStyles;
    productTotalPriceHeadingAndText?: SmallHeadingAndTextStyles;
    actionsContainer?: InjectableCss;
    priceText?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    priceWrapper?: InjectableCss;
    priceHeading?: TypographyProps;
    qtyToReturn?: QtyFieldStyles;
    noPrice?: SmallHeadingAndTextStyles;
}

export const requestRmaProductListStyles: RequestRmaProductListStyles = {
    gridContainer: {
        css: css`
            margin-top: 35px;
            margin-bottom: 25px;
        `,
    },
    orderLineCardImageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
        css: css`
            padding-left: 0;
        `,
    },
    orderLineCardInfoGridItem: { width: [9, 9, 7, 7, 8] },
    orderLineCardInfoGridContainer: { gap: 10 },
    returnReasonGridItem: {
        width: [9, 9, 3, 3, 3],
        printWidth: 0,
        css: css`
            flex-direction: column;
            margin-left: auto !important;
        `,
    },
    qtyToReturnTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 90px;
            `,
        },
    },
    productInfoGridItem: { width: [12, 12, 8, 8, 8] },
    productInfoGridContainer: { gap: 5 },
    productInfoBrandDescriptionGridItem: { width: 12 },
    productInfoBrandDescriptionGridContainer: { gap: 5 },
    productInfoBrandGridItem: { width: 12 },
    productInfoDescriptionGridItem: { width: 12 },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbers: {
        erpNumberLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        customerNameLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
    },
    orderLineInfoGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
    },
    orderLineInfoGridContainer: { gap: 10 },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
    },
    productInfoSubtotalGridItem: { width: 12 },
};

// XNG-Changes: update structure
const ProductInfo = ({ orderLine }: { orderLine: OrderLineModel }) => {
    return (
        <>
            {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />}
            <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            <ProductPartNumbers
                productNumber={orderLine.productErpNumber}
                // Xng-Changes
                customerProductNumber={orderLine.customerName}
                manufacturerItem={orderLine.manufacturerItem}
                extendedStyles={styles.productPartNumbers}
            />
        </>
    );
};

// XNG Change: split qty, price and total in the same component
const OrderLineInfo = ({ orderLine, hidePrices }: { orderLine: OrderLineModel; hidePrices: boolean }) => {
    return {
        Price: (
            <StyledWrapper {...styles.priceWrapper}>
                <Typography {...styles.priceHeading}>{translate("Price/Unit")}</Typography>
                <Typography className={hidePrices ? "contracted-price" : ""} {...styles.priceText}>
                    {orderLine.unitPriceDisplay}
                </Typography>
                <Typography {...styles.unitOfMeasure}>
                    {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                </Typography>
            </StyledWrapper>
        ),
        Qty: <QtyField value={orderLine.qtyOrdered} editable={false} />,
        Total: (
            <SmallHeadingAndText
                {...(hidePrices ? { ...styles.noPrice } : { ...styles.productTotalPriceHeadingAndText })}
                heading={translate("Total")}
                text={orderLine.extendedUnitNetPriceDisplay}
            />
        ),
    };
};

const OrderLineCard = (props: {
    order: OrderModel;
    orderLine: OrderLineModel;
    totalQuantity: number;
    qtyChangeHandler: (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => void;
    returnReasonChangeHandler: (event: React.ChangeEvent<HTMLSelectElement>, lineNumber: number) => void;
    hidePrices: boolean;
}) => {
    const { order, orderLine, totalQuantity, qtyChangeHandler, returnReasonChangeHandler, hidePrices } = props;
    const minQtyToReturn = totalQuantity > 0 ? 0 : 1;
    const qtyToReturnError =
        totalQuantity > 0 &&
        ((orderLine.rmaQtyRequested > orderLine.qtyOrdered &&
            siteMessage("Field_Max_Number", translate("QTY Returning"), orderLine.qtyOrdered.toString())) ||
            (orderLine.rmaQtyRequested < minQtyToReturn &&
                siteMessage("Field_Min_Number", translate("QTY Returning"), minQtyToReturn.toString())));

    // XNG-Changes
    const orderLineInfo = OrderLineInfo({
        orderLine,
        hidePrices,
    });

    // XNG-Changes
    return (
        <CartLineContainer>
            <CartLineContainer.Image>
                {orderLine.productUri && orderLine.isActiveProduct && (
                    <Link href={orderLine.productUri} aria-label="Product Image">
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            altText={orderLine.altText}
                        />
                    </Link>
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <ProductInfo orderLine={orderLine} />
            </CartLineContainer.Description>
            <CartLineContainer.Price>{orderLineInfo.Price}</CartLineContainer.Price>
            <CartLineContainer.Qty>{orderLineInfo.Qty}</CartLineContainer.Qty>
            <CartLineContainer.Total>{orderLineInfo.Total}</CartLineContainer.Total>
            <CartLineContainer.Actions>
                {/* XNG-Changes: fix actions vertical alignment and style on mobile */}
                <StyledWrapper {...styles.actionsContainer}>
                    {/* Xng-Changes QtyField */}
                    <QtyField
                        extendedStyles={styles.qtyToReturn}
                        label={translate("QTY to Return")}
                        type="qty"
                        value={orderLine.rmaQtyRequested.toString()}
                        min={minQtyToReturn}
                        max={orderLine.qtyOrdered}
                        error={qtyToReturnError}
                        data-test-selector={`requestRmaLine_qtyToReturn_${orderLine.id}`}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            qtyChangeHandler(event, orderLine.lineNumber)
                        }
                        allowZero={true}
                    />
                    {!!order.returnReasons?.length && (
                        <Select
                            label={translate("Return Reason")}
                            {...styles.returnReasonSelect}
                            value={orderLine.returnReason || ""}
                            required={orderLine.rmaQtyRequested > 0}
                            error={
                                orderLine.rmaQtyRequested > 0 &&
                                !orderLine.returnReason &&
                                siteMessage("Field_Required", translate("Return Reason"))
                            }
                            data-test-selector={`requestRmaLine_ReturnReason_${orderLine.id}`}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                returnReasonChangeHandler(event, orderLine.lineNumber)
                            }
                        >
                            <option value="">{translate("Select a Reason Code")}</option>
                            {order.returnReasons?.map(returnReason => (
                                <option key={returnReason} value={returnReason}>
                                    {returnReason}
                                </option>
                            ))}
                        </Select>
                    )}
                </StyledWrapper>
            </CartLineContainer.Actions>
        </CartLineContainer>
    );
};

const styles = requestRmaProductListStyles;

const RequestRmaProductList: FC<Props> = ({ orderLines, setOrderLines, setCanSendReturnRequest, hidePrices }) => {
    const [totalQuantity, setTotalQuantity] = React.useState(0);
    const { value: order } = useContext(OrderStateContext);

    React.useEffect(() => {
        if (!order || !order.orderLines) {
            return;
        }

        setOrderLines({ orderLines: order.orderLines });
    }, [order?.orderLines]);

    const reasonsDefined = !!order?.returnReasons?.length;
    React.useEffect(() => {
        setCanSendReturnRequest({
            value:
                orderLines.some(line => line.rmaQtyRequested > 0 && (line.returnReason || !reasonsDefined)) &&
                !orderLines.some(
                    line =>
                        line.rmaQtyRequested > line.qtyOrdered ||
                        (line.rmaQtyRequested > 0 && !line.returnReason && reasonsDefined),
                ),
        });
    }, [orderLines]);

    if (!order || !order.orderLines) {
        return null;
    }

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => {
        let qty = parseInt(event.target.value, 10);
        if (event.target.value === "") {
            qty = 0;
        }
        const updatedOrderLines = orderLines.map(line =>
            line.lineNumber === lineNumber ? { ...line, rmaQtyRequested: qty } : line,
        );
        setOrderLines({ orderLines: updatedOrderLines });

        let totalQty = 0;
        updatedOrderLines.forEach(orderLine => {
            totalQty += orderLine.rmaQtyRequested > 0 ? 1 : 0;
        });

        setTotalQuantity(totalQty);
    };

    const returnReasonChangeHandler = (event: React.FormEvent<HTMLSelectElement>, lineNumber: number) => {
        setOrderLines({
            orderLines: orderLines.map(line =>
                line.lineNumber === lineNumber ? { ...line, returnReason: event.currentTarget.value } : line,
            ),
        });
    };

    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector="requestRmaProductList">
            {!!orderLines.length && (
                <CartLinesListHeader isCondensed={false} hideCondensedSelector productsCount={orderLines.length} />
            )}
            <LinesHeader includeActions={false} />
            {orderLines.map(orderLine => (
                <OrderLineCard
                    key={orderLine.id}
                    order={order}
                    orderLine={orderLine}
                    totalQuantity={totalQuantity}
                    qtyChangeHandler={qtyChangeHandler}
                    returnReasonChangeHandler={returnReasonChangeHandler}
                    hidePrices={hidePrices}
                />
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaProductList),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
